// Single source of truth for every runtime constant, lookup table, and game rule.
// shared/types.ts derives its enum types from these arrays and re-exports them
// so existing imports keep working unchanged.

// ── Tiers ─────────────────────────────────────────────────────────────────────

export const TIERS = [
  "white",
  "yellow",
  "pink",
  "green",
  "orange",
  "blue",
  "gray",
  "purple",
  "red",
  "black",
] as const;

export const TIER_ORDER = {
  white: 0,
  yellow: 1,
  pink: 2,
  green: 3,
  orange: 4,
  blue: 5,
  gray: 6,
  purple: 7,
  red: 8,
  black: 9,
} satisfies Record<(typeof TIERS)[number], number>;

// ── Race categories ───────────────────────────────────────────────────────────

export const RACE_TYPES = ["road", "street", "cross_country", "dirt"] as const;

export const CAR_CLASSES = ["d", "c", "b", "a", "s1", "s2", "x", "r"] as const;

// Display labels: race types title-cased (with spaces), car classes upper-cased.
// Capitalized for display per the domain convention; the lowercase forms are the
// DB enum values. Single source of truth — the frontend (~/lib/utils) re-exports
// these and the Discord bot imports them, instead of each redefining the map.
export const RACE_TYPE_LABELS = {
  road: "Road",
  street: "Street",
  cross_country: "Cross Country",
  dirt: "Dirt",
} satisfies Record<(typeof RACE_TYPES)[number], string>;

export const CAR_CLASS_LABELS = {
  d: "D",
  c: "C",
  b: "B",
  a: "A",
  s1: "S1",
  s2: "S2",
  x: "X",
  r: "R",
} satisfies Record<(typeof CAR_CLASSES)[number], string>;

// "cross_country", "s1" → "Cross Country S1". Takes widened string fields (DB
// rows, history entries) and falls back to the raw value for unknown keys.
export function formatCategory(raceType: string, carClass: string): string {
  const race =
    RACE_TYPE_LABELS[raceType as (typeof RACE_TYPES)[number]] ?? raceType;
  const cls =
    CAR_CLASS_LABELS[carClass as (typeof CAR_CLASSES)[number]] ??
    carClass.toUpperCase();
  return `${race} ${cls}`;
}

// Upper-cases the first letter only: "white" → "White". For tier names and other
// single tokens shown in prose.
export function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// ── Statuses ──────────────────────────────────────────────────────────────────

export const DUEL_STATUSES = [
  "pending",
  "active",
  "completed",
  "expired",
  "declined",
  "disputed",
] as const;

// Badge tone per duel status (a draw overrides it with "warning" at the call site).
// Literal Badge variant names, validated where <Badge> consumes them.
export const DUEL_STATUS_VARIANT = {
  pending: "pending",
  active: "brand",
  completed: "success",
  expired: "default",
  declined: "default",
  disputed: "danger",
} as const satisfies Record<(typeof DUEL_STATUSES)[number], string>;

export const USER_ROLES = ["member", "judge", "admin"] as const;

export const REPORT_STATUSES = [
  "open",
  "under_review",
  "resolved",
  "dismissed",
  "escalated",
] as const;

export const TOURNAMENT_STATUSES = [
  "upcoming",
  "active",
  "completed",
  "cancelled",
] as const;

// ── Reports ───────────────────────────────────────────────────────────────────

// Badge tone for each report status. The literal values are Badge variant names,
// validated where <Badge> consumes them; kept here, not in the UI, so status →
// severity stays one domain decision shared by every surface that renders a report.
export const REPORT_STATUS_VARIANT = {
  open: "danger",
  under_review: "warning",
  resolved: "success",
  dismissed: "default",
  escalated: "danger",
} as const satisfies Record<(typeof REPORT_STATUSES)[number], string>;

// Who may act on a report, mirroring the server's 403 rules: a participant in the
// underlying duel can never act (self-recusal); only the assigned judge concludes an
// under-review case, and only an admin concludes an escalated one. Structurally typed
// so it stays in the domain layer without importing the Report view-model.
export function reportPermissions(
  report: {
    status: (typeof REPORT_STATUSES)[number];
    assignedJudgeId: string | null;
    duel?: { attackerId: string; defenderId: string } | null;
  },
  user: { id?: string; role?: (typeof USER_ROLES)[number] } | null | undefined,
): {
  isParticipant: boolean;
  isMine: boolean;
  isAdmin: boolean;
  isActionable: boolean;
  canConclude: boolean;
} {
  const me = user?.id;
  const d = report.duel;
  const isParticipant =
    !!d && !!me && (d.attackerId === me || d.defenderId === me);
  const isMine = !!report.assignedJudgeId && report.assignedJudgeId === me;
  const isAdmin = user?.role === "admin";
  const isActionable =
    report.status === "open" ||
    report.status === "under_review" ||
    report.status === "escalated";
  const canConclude =
    !isParticipant &&
    ((report.status === "under_review" && isMine) ||
      (report.status === "escalated" && isAdmin));
  return { isParticipant, isMine, isAdmin, isActionable, canConclude };
}

// ── Duel recording ────────────────────────────────────────────────────────────

export const RECORDING_MODES = ["full", "summary"] as const;

// ── Duel timing ───────────────────────────────────────────────────────────────

// How long the defender has to accept or decline a challenge.
export const RESPOND_DEADLINE_MS = 48 * 60 * 60 * 1000;

// Maximum duration of an active duel from the moment it is accepted.
export const DUEL_DEADLINE_MS = 14 * 24 * 60 * 60 * 1000;

// How far in advance of the respond deadline a reminder is sent.
export const REMINDER_WINDOW_MS = 6 * 60 * 60 * 1000;

// ── Duel outcome ──────────────────────────────────────────────────────────────

// First player to lead by this many race wins claims victory.
export const WIN_LEAD = 3;

// Minimum individual race wins each player must have before a drawn result is valid.
// Prevents low-effort 0–0 or 1–1 submissions from being accepted as ties.
export const TIE_MIN_SCORE = 10;

// Classifies a final score. A duel only resolves on a legal terminal result: a decisive
// win (the margin reaches WIN_LEAD) or a valid draw (level, at least TIE_MIN_SCORE each).
// Anything else is an unfinished duel, not a result - the single rule both recording
// modes finalize against, kept pure so the service and its tests share one definition.
export type DuelScoreOutcome =
  | { kind: "win"; winner: "attacker" | "defender" }
  | { kind: "draw" }
  | { kind: "incomplete"; reason: string };

export function duelScoreOutcome(
  attackerScore: number,
  defenderScore: number,
): DuelScoreOutcome {
  if (attackerScore === defenderScore) {
    return attackerScore >= TIE_MIN_SCORE
      ? { kind: "draw" }
      : {
          kind: "incomplete",
          reason: `A draw needs at least ${TIE_MIN_SCORE} race wins each — ${attackerScore}–${defenderScore} isn't enough.`,
        };
  }
  const lead = Math.abs(attackerScore - defenderScore);
  if (lead < WIN_LEAD) {
    return {
      kind: "incomplete",
      reason: `A win needs a lead of at least ${WIN_LEAD} races — ${attackerScore}–${defenderScore} is only +${lead}.`,
    };
  }
  return {
    kind: "win",
    winner: attackerScore > defenderScore ? "attacker" : "defender",
  };
}

// ── Cooldowns ─────────────────────────────────────────────────────────────────

// After losing a tier challenge, the attacker cannot challenge the same tier for this long.
export const TIER_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;

// After losing to a player, the loser must challenge this many other drivers before a rematch.
export const PLAYER_COOLDOWN_COUNT = 2;

// ── Vacation vouchers ─────────────────────────────────────────────────────────

// Maximum number of unused vouchers a player can hold at once.
export const MAX_VOUCHERS = 3;

// How long an activated voucher protects a player from challenges.
export const VOUCHER_DURATION_MS = 7 * 24 * 60 * 60 * 1000;

// The voucher balance may run negative as a debt (Phase 18 same-tier enforcement).
// Reaching this floor triggers a "vacation bankruptcy": a one-tier demotion across
// all categories, after which the debt resets to zero. A `system_config` knob; this
// is the default. A voucher mechanic, separate from the keppan streak it enforces,
// hence the voucher-scoped name.
export const VOUCHER_DEBT_FLOOR = -3;

// The kinds of line in the append-only voucher ledger. Each carries a signed amount
// (grant +1; vacation_spend / dodge_strip −1; admin_grant / admin_retract ±N;
// bankruptcy +|VOUCHER_DEBT_FLOOR|; vacation_cancel 0, it only ends a vacation
// window). The ledger is the source of truth; current state is folded from it.
export type VoucherEventKind =
  | "grant"
  | "vacation_spend"
  | "vacation_cancel"
  | "dodge_strip"
  | "admin_grant"
  | "admin_retract"
  | "bankruptcy";

// Project a driver's voucher ledger (chronological) into current state. The balance
// is the running sum of signed amounts; available is its positive region, debt its
// negative. The active vacation window is the latest decline_spend's expiry, unless a
// later attack_invalidate cancelled it or it has already lapsed. Pure: the ledger is
// the source of truth, this is its derived projection (no stored balance).
export function deriveVoucherState(
  lines: readonly {
    kind: VoucherEventKind;
    amount: number;
    expiresAt: Date | null;
    createdAt: Date;
  }[],
  now: Date = new Date(),
): {
  balance: number;
  available: number;
  debt: number;
  vacationUntil: Date | null;
} {
  let balance = 0;
  let activeUntil: Date | null = null;
  for (const line of lines) {
    balance += line.amount;
    if (line.kind === "vacation_spend") activeUntil = line.expiresAt;
    else if (line.kind === "vacation_cancel") activeUntil = null;
  }
  return {
    balance,
    available: Math.max(0, balance),
    debt: Math.max(0, -balance),
    vacationUntil: activeUntil && activeUntil > now ? activeUntil : null,
  };
}

// ── Tournaments ───────────────────────────────────────────────────────────────

export const PROMOTION_MODES = [
  "super",
  "ladder",
  "generous",
  "strict",
  "elite",
  "showdown",
] as const;

export const PROMOTION_MODE_LABELS = {
  generous: "Generous",
  strict: "Strict",
  elite: "Elite (Top Drivers Only)",
  showdown: "Showdown (Winner Only)",
  ladder: "Ladder",
  super: "SUPER PROMOTION",
} satisfies Record<(typeof PROMOTION_MODES)[number], string>;

export const PROMOTION_MODE_DESCRIPTIONS = {
  generous: "Top 50% of finishers advance (rounded up).",
  strict: "Top 50% of finishers advance (rounded down).",
  elite:
    "Drivers holding the top 50% of all points advance. Usually only the top 2–4 performers.",
  showdown: "Only the winner advances, the single top-ranked finisher.",
  ladder:
    "The field splits into thirds by rank: the top third jump two tiers, the middle third - one, the bottom third - none.",
  super:
    "Everyone advances. The field splits into thirds by rank: the top third jump three tiers, the middle third - two, the bottom third - one.",
} satisfies Record<(typeof PROMOTION_MODES)[number], string>;

// The tournament standings tiebreak chain, stated once for every surface that
// explains it. The server's comparator (standingsComparator) implements exactly
// this order, so standings can never tie in any mode.
export const STANDINGS_TIEBREAK_RULE =
  "Standings never tie. Drivers with equal points are split by countback: most wins, then most 2nd places, and so on. What countback cannot split, the decider race settles - the highest-multiplier race first, then the latest race. Between equal records, a comeback beats an early lead.";

export const DEFAULT_POINTS_SCALE = [
  20, 16, 14, 12, 10, 8, 6, 5, 4, 3, 2, 1,
] as const;

// Vacation vouchers awarded to podium finishers, by placement (index 0 = 1st).
// Graduated so the winner earns the most; finishers past the podium earn none.
export const TOURNAMENT_VOUCHER_AWARDS = [3, 2, 1] as const;

// Podium vouchers are only minted once a tournament draws this many participants;
// smaller fields award none, so a three-person bracket can't farm protection.
export const VOUCHER_MIN_PARTICIPANTS = 5;

// Vouchers a podium finisher earns: graduated by placement, but only in fields of
// VOUCHER_MIN_PARTICIPANTS or more. Tied placements share a position (1, 1, 3…),
// so co-winners each take the higher award and the skipped slot pays out nothing.
export function tournamentVouchersFor(
  placement: number,
  participantCount: number,
): number {
  if (participantCount < VOUCHER_MIN_PARTICIPANTS) return 0;
  return TOURNAMENT_VOUCHER_AWARDS[placement - 1] ?? 0;
}

// The tier one step below the given tier (clamped at white). Used to derive a
// single-target tournament's accepted tier from the Discord announcement format.
export function tierBelow(
  tier: (typeof TIERS)[number],
): (typeof TIERS)[number] {
  return TIERS[Math.max(0, TIER_ORDER[tier] - 1)];
}

// The tier a driver lands on after climbing `steps` from their own tier, clamped
// at the top tier. Mirrors the driver-relative promotion the completion pipeline
// applies, so a mixed-tier field is projected the same way a preview draws it.
export function promoteTier(
  tier: (typeof TIERS)[number],
  steps: number,
): (typeof TIERS)[number] {
  return TIERS[Math.min(TIER_ORDER[tier] + steps, TIERS.length - 1)];
}

// The advancing cutoff the two half-field modes share: the top half rounded up for
// Generous, down for Strict. One definition so the preview count and the server's
// cutoff can't drift. Generous may still promote past it once boundary ties fold in.
export function advancerCount(mode: "generous" | "strict", n: number): number {
  return mode === "generous" ? Math.ceil(n / 2) : Math.floor(n / 2);
}

const SUPER_STEPS = [3, 2, 1] as const;
const LADDER_STEPS = [2, 1, 0] as const;
// The tercile split Ladder and Super share, kept in one place
// so the promotion preview can never diverge from what completion actually does.
// Ranks are cut at floor(n/3) and floor(2n/3):
// an uneven field feeds its spare drivers to the lower terciles,
// and the bigger the jump, the fewer drivers make it.
// Favoring the nearer tiers keeps them populated,
// so drivers who sat the tournament out still find someone to challenge.
// resolvePromotionSteps maps the cuts onto placements so boundary ties share a third,
// while promotionBrackets maps them onto raw finishing order for the infographic.
export function tercileSplit(
  mode: "ladder" | "super",
  n: number,
): {
  cuts: readonly [number, number];
  steps: readonly [number, number, number];
} {
  return {
    cuts: [Math.floor(n / 3), Math.floor((2 * n) / 3)],
    steps: mode === "super" ? SUPER_STEPS : LADDER_STEPS,
  };
}

// How a field of `n` finishers splits under a promotion mode: the ordered brackets
// from best to worst finish, each with how many drivers fall in it and the tier
// step they climb. The order is load-bearing, since a preview reads each bracket's
// finishing range off the running count. This is the shape the preview infographics
// draw. The authoritative selection is points-and-countback-based and lives
// server-side (resolvePromotionSteps); it can differ at the margins, since Elite is
// point-weighted and boundary ties promote together. Steps mirror that logic: the
// binary modes climb a single tier, Ladder and Super grade by tercile.
export type PromotionBracket = { count: number; step: number };

export function promotionBrackets(
  mode: (typeof PROMOTION_MODES)[number],
  n: number,
): PromotionBracket[] {
  if (n <= 0) return [];
  const rest = (advancing: number) => Math.max(0, n - advancing);

  if (mode === "showdown") {
    return [
      { count: 1, step: 1 },
      { count: rest(1), step: 0 },
    ];
  }
  if (mode === "generous" || mode === "strict") {
    const advance = advancerCount(mode, n);
    return [
      { count: advance, step: 1 },
      { count: rest(advance), step: 0 },
    ];
  }
  if (mode === "elite") {
    // Point-weighted on the day; illustrated as the leading third, the usual two
    // to four drivers who hold half the points between them.
    const advance = Math.max(1, Math.round(n / 3));
    return [
      { count: advance, step: 1 },
      { count: rest(advance), step: 0 },
    ];
  }

  // Ladder and Super split into three terciles via the shared rule,
  // spare drivers falling to the smaller jumps.
  const {
    cuts: [topCut, midCut],
    steps: [topStep, midStep, bottomStep],
  } = tercileSplit(mode, n);
  return [
    { count: topCut, step: topStep },
    { count: midCut - topCut, step: midStep },
    { count: n - midCut, step: bottomStep },
  ];
}

// A driver may register if their current tier is one of the tournament's accepted
// (eligible) tiers. Tournaments now accept from an explicit set of tiers rather
// than a single one-below tier; promotion is driver-relative (each winner moves up
// from their own rank), so a mixed-tier field promotes correctly.
export function isEligibleForTournament(
  playerTier: (typeof TIERS)[number],
  eligibleTiers: readonly (typeof TIERS)[number][],
): boolean {
  return eligibleTiers.includes(playerTier);
}

// The categories a driver can challenge into and the tier they'd target: exactly
// one tier above each of their rankings. White counts (white → yellow), so a
// driver still has rivals in disciplines they haven't ranked up in; black drops
// out - nothing sits above it. Feeds the rivals carousel and the challenge gate.
export function rivalTierTargets(
  rankings: readonly {
    raceType: (typeof RACE_TYPES)[number];
    carClass: (typeof CAR_CLASSES)[number];
    tier: (typeof TIERS)[number];
  }[],
): {
  raceType: (typeof RACE_TYPES)[number];
  carClass: (typeof CAR_CLASSES)[number];
  rivalTier: (typeof TIERS)[number];
}[] {
  return rankings.flatMap((r) => {
    const above = TIERS[TIER_ORDER[r.tier] + 1];
    return above
      ? [{ raceType: r.raceType, carClass: r.carClass, rivalTier: above }]
      : [];
  });
}

// Whether the caller may challenge a given rival right now - the rule the rivals
// carousel applies. Three blocks: the caller or the rival is already in a duel; a
// live tier cooldown covers the rival's tier; or an unsatisfied player cooldown
// gates a rematch. A player cooldown counts down to 0, so challengesRemaining > 0
// still blocks and 0 is clear - fed the raw cooldown rows so that rule is testable
// here rather than buried in a SQL predicate.
export function isRivalChallengeable(input: {
  callerHasActiveDuel: boolean;
  rivalHasActiveDuel: boolean;
  rivalTier: (typeof TIERS)[number];
  rivalUserId: string;
  cooldownTiers: readonly (typeof TIERS)[number][];
  playerCooldowns: readonly { targetId: string; challengesRemaining: number }[];
}): boolean {
  if (input.callerHasActiveDuel || input.rivalHasActiveDuel) return false;
  if (input.cooldownTiers.includes(input.rivalTier)) return false;
  if (
    input.playerCooldowns.some(
      (c) => c.targetId === input.rivalUserId && c.challengesRemaining > 0,
    )
  )
    return false;
  return true;
}

// ── Keppan, the same-tier challenge streak (user-facing: "Blood Stamps") ───────

// Default number of distinct peers to defeat for a clean sweep. A runtime
// `system_config` knob overrides it; this is the seed/default and the test value.
export const KEPPAN_THRESHOLD = 3;

// One same-tier duel folded into a driver's current keppan run, applied to BOTH
// drivers afterward (winner with won=true, loser with won=false). The run is the SET
// of distinct peers beaten without a loss in between, never a bare count, so
// distinctness is structural. A win over a new peer presses a stamp; a win over
// someone already in the run does not double-count; any same-tier loss spills the
// whole collection. Reaching the threshold sacrifices the stamps to ascend, so the
// set resets for the new tier.
export function applyKeppan(
  current: readonly string[],
  result: { opponentId: string; won: boolean },
  threshold: number = KEPPAN_THRESHOLD,
): { stamps: string[]; ascended: boolean } {
  if (!result.won) return { stamps: [], ascended: false };
  const stamps = current.includes(result.opponentId)
    ? [...current]
    : [...current, result.opponentId];
  if (stamps.length >= threshold) return { stamps: [], ascended: true };
  return { stamps, ascended: false };
}

// Fold a chronological run of same-tier results into the current keppan run, the
// pure core of the eager-cache derivation. Each result is one finalized, *decisive*
// Δ0 duel for the driver (`won` = they won, `opponentId` = the peer); draws must be
// filtered out before folding, since a draw is a no-op, not a loss. Delegates every
// rule decision to applyKeppan (grow on a new peer, ignore a re-defeat, spill on a
// loss, sacrifice on the threshold) and mirrors that onto the caller's own result
// objects, so the surviving stamps come back carrying whatever provenance the caller
// threaded through (the earning duel and its time), ready to write to the cache. The
// final `ascended` is the last result's, which is the only step that can trip the
// threshold within a single tier stint.
export function keppanRunFromResults<
  T extends { opponentId: string; won: boolean },
>(
  results: readonly T[],
  threshold: number = KEPPAN_THRESHOLD,
): { stamps: T[]; ascended: boolean } {
  let stamps: T[] = [];
  let ascended = false;
  for (const r of results) {
    const ids = stamps.map((s) => s.opponentId);
    const step = applyKeppan(
      ids,
      { opponentId: r.opponentId, won: r.won },
      threshold,
    );
    ascended = step.ascended;
    if (step.stamps.length === 0) stamps = [];
    else if (!ids.includes(r.opponentId)) stamps = [...stamps, r];
  }
  return { stamps, ascended };
}

// The blood-seal family: a duel's type is a pure function of the tier gap from the
// attacker up to the defender. 同輩 (dohai, delta 0) is a same-tier peer duel and the
// only type the keppan streak counts; 格上 (kakuue, delta 1) is the binding
// one-tier-up challenge; 豪傑 (goketsu, delta 2+) is a giant-kill (Phase 19). This
// types the matchup itself (attacker-relative, outcome-independent), which is what a
// duel-page emboss and a giant-kill history badge both need; whether a seal was
// *earned* (the winner beat someone at or above them) is a separate predicate the
// streak/promotion logic owns. It is the single shared primitive across every surface
// that renders a seal, so the type is never stored, only derived from snapshotted
// tiers. A defender-below gap (delta < 0, not a sanctioned challenge) reads as dohai.
export type SealType = "dohai" | "kakuue" | "goketsu";

// ── Blood-seal (challenge band) labels ──────────────────────────────────────────
// English label + kanji for each matchup band from the caller's seat: Δ0 同輩 peers,
// Δ1 格上 rivals, Δ2+ 豪傑 giants, plus a one-line explanation of what the band means.
// The single display source shared by the rivals carousel (filter chips + per-card tag)
// and the rankings-table challengeable tag (whose hint reads from `hint`).
export const SEAL_LABELS = {
  dohai: {
    label: "Peers",
    kanji: "同輩",
    hint: "A peer at your tier. Beating distinct peers builds your Blood Stamps, and a clean run lifts you a tier.",
  },
  kakuue: {
    label: "Rivals",
    kanji: "格上",
    hint: "One tier above you. A binding challenge, ignoring it demotes them, and a win lifts you to their tier.",
  },
  goketsu: {
    label: "Giants",
    kanji: "豪傑",
    hint: "Two or more tiers above you. A giant-kill they may refuse for free, but a win carries you up to them.",
  },
} satisfies Record<SealType, { label: string; kanji: string; hint: string }>;

export function sealType(
  attackerTier: (typeof TIERS)[number],
  defenderTier: (typeof TIERS)[number],
): SealType;
export function sealType(
  attackerTier?: (typeof TIERS)[number] | null | undefined,
  defenderTier?: (typeof TIERS)[number] | null | undefined,
): SealType | undefined;
export function sealType(
  attackerTier?: (typeof TIERS)[number] | null | undefined,
  defenderTier?: (typeof TIERS)[number] | null | undefined,
): SealType | undefined {
  if (!attackerTier || !defenderTier) return undefined;

  const delta = TIER_ORDER[defenderTier] - TIER_ORDER[attackerTier];
  if (delta <= 0) return "dohai";
  if (delta === 1) return "kakuue";
  return "goketsu";
}

// ── Refusal policy (the single place the "what does refusing cost" rules live) ──
// Pure decisions the duel service routes through, so the rules can be tuned here without
// digging into the service. `forced` is the judge-forced flag on a duel: a giant-kill the
// challenger reported and a judge compelled to be raced.

// A defender owes nothing for refusing this challenge, so they may decline or ignore it
// for free: a giant-kill (2+ tiers up) from far below that a judge has NOT forced binding.
// Same-tier and one-up challenges are never free to refuse, and a forced giant-kill is
// binding like a one-up.
export function owesNothingToRefuse(seal: SealType, forced: boolean): boolean {
  return seal === "goketsu" && !forced;
}

// Once a refusal is not free, ignoring the challenge (a dodge) costs the defender:
//  - "strip": a same-tier (dohai) dodge force-strips a vacation voucher, overdraft toward
//    bankruptcy at the floor;
//  - "demote": a one-up (kakuue) dodge, or a dodge of a forced giant-kill, is a one-tier
//    no-show demotion.
export type DodgePenalty = "strip" | "demote";
export function dodgePenalty(seal: SealType): DodgePenalty {
  return seal === "dohai" ? "strip" : "demote";
}

// ── CRG rules text ────────────────────────────────────────────────────────────
// Human-readable descriptions of each rule, grouped by topic.
// Match the numeric constants above - update both if a rule changes.
// TODO: pull these from Discord channels (same approach as /api/rules) so they
// stay in sync with the live ruleset without requiring a code change.

export const CRG_RULES = {
  challenges: {
    label: "Challenges",
    items: [
      "You may challenge a peer at your own tier, a driver one tier above, or a driver two or more tiers above, all within the same category.",
      `Same-tier wins build a Blood Stamps run, and beating ${KEPPAN_THRESHOLD} distinct peers with no loss in between promotes you a tier. Any same-tier loss spills the whole run.`,
      "A one-tier-up challenge is binding. The defender has 48 hours to accept, ignoring it counts as a no-show and demotes them, and a win lifts you a tier.",
      "A challenge two or more tiers up is a giant-kill. The defender may decline or ignore it freely at no cost, and a win carries you up to their tier while they fall one. Earning the right to swing that high is a matter for the judges.",
      "Declining a same-tier or one-up challenge spends an unused vacation voucher, which activates your protection at once.",
      `Ignoring a same-tier challenge strips a voucher and grants no protection. It can push your balance into debt, and a debt of ${-VOUCHER_DEBT_FLOOR} demotes you across every category.`,
      "You cannot challenge while you have a pending or active duel.",
      "Attacking while holding an active vacation voucher cancels your protection.",
      "After losing an upward challenge, you cannot attack that tier again for 7 days.",
      `After losing to a specific driver, you must challenge ${PLAYER_COOLDOWN_COUNT} other drivers before a rematch.`,
    ],
  },
  duels: {
    label: "Duels",
    items: [
      `First to lead by ${WIN_LEAD} race wins claims victory — a 1–0 or 2–0 deficit can still be turned around.`,
      "Active duels must be completed within 14 days of acceptance.",
      `A drawn result (equal total wins) is only valid if both drivers have won at least ${TIE_MIN_SCORE} races each.`,
      "In a confirmed draw, the defender retains their tier.",
    ],
  },
  vouchers: {
    label: "Vacation Vouchers",
    items: [
      `You may hold up to ${MAX_VOUCHERS} unused vouchers at once.`,
      "An activated voucher protects you from challenges for 7 days.",
      `Duel winners earn one voucher. In tournaments of ${VOUCHER_MIN_PARTICIPANTS}+ drivers, the podium earns 3 (1st), 2 (2nd), and 1 (3rd).`,
      "Attacking while protected cancels your own voucher.",
      "Ignoring same-tier challenges can drive your balance into debt, and anything you earn next pays the debt down before it counts.",
    ],
  },
  tournaments: {
    label: "Tournaments",
    items: [
      "You can register if your current tier is one of the tournament's accepted tiers.",
      "Promoted drivers advance one or more tiers above their current rank after completion, depending on the mode.",
      "A race may carry a points multiplier, so a finale can pay double points.",
      STANDINGS_TIEBREAK_RULE,
      `In fields of ${VOUCHER_MIN_PARTICIPANTS}+ drivers, the podium earns vouchers (3 / 2 / 1 for 1st / 2nd / 3rd), regardless of promotion.`,
    ],
  },
  promotionModes: {
    label: "Tournament Promotion Modes",
    items: Object.values(PROMOTION_MODE_DESCRIPTIONS),
  },
} as const;
